import React, { useEffect, useState, createContext, useContext, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { FaBook, FaUser, FaPaperPlane, FaDownload, FaUserShield } from 'react-icons/fa';
import './App.css';
import Home from './pages/Home';
import Help from './pages/Help';
import Subscriptions from './pages/Subscriptions';
import Profile from './pages/Profile';
import Support from './pages/Support';
import Download from './pages/Download';
import Admin from './pages/Admin';
import Logo from './components/Logo';

export const UserContext = createContext(null);
export const UserUpdaterContext = createContext(null);

export const useUser = () => useContext(UserContext);
export const useUserUpdater = () => useContext(UserUpdaterContext);

function AppContent() {
  const tg = window.Telegram?.WebApp;
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserData = useCallback(async () => {
    if (!tg) {
      setError("Telegram WebApp API is not available.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/telegram', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'init-data': tg.initData,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(`Server error: ${response.status}. ${errorData?.detail || ''}`);
      }

      const userData = await response.json();
      setUser(userData);

      // --- Логика проверки успешной оплаты ---
      const paymentInProgress = localStorage.getItem('paymentInProgress');
      if (paymentInProgress === 'true') {
        const oldCount = parseInt(localStorage.getItem('keyCountBeforePayment') || '0', 10);
        const newCount = userData.keys?.length || 0;
        
        if (newCount > oldCount) {
          tg.showAlert('Оплата прошла успешно! Ваш новый ключ уже ждет вас в разделе \'Кабинет\'.');
          navigate('/profile');
        }
        
        // Снимаем флаги после проверки
        localStorage.removeItem('paymentInProgress');
        localStorage.removeItem('keyCountBeforePayment');
      }
      // --- Конец логики ---

    } catch (err) {
      console.error("Authentication failed:", err);
      setError(`Authentication failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [tg, navigate]);

  useEffect(() => {
    if (tg) {
      tg.ready();
      tg.expand();
      fetchUserData();
    } else if (process.env.NODE_ENV === 'development') {
      // --- Мок-данные для разработки на ПК ---
      console.log("Running in development mode without Telegram, using mock user.");
      setUser({
        id: 922461349,
        telegram_id: 922461349,
        first_name: "Dev",
        last_name: "User",
        username: "dev_user",
        photo_url: "https://t.me/i/userpic/320/dev_user.jpg",
        is_admin: true,
        referral_code: 'DEV12345',
        bonus_balance: 100,
        has_active_subscription: true,
        subscription_end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      });
      // --- Конец мок-данных ---
    }
  }, [tg, fetchUserData]);

  if (loading) {
    return <div className="loading-container">Loading user data...</div>;
  }

  if (error) {
    return <div className="error-container">Error: {error}</div>;
  }

  return (
    <UserContext.Provider value={user}>
      <UserUpdaterContext.Provider value={fetchUserData}>
        <div className="App">
          <div className="App-content">
            <Routes>
              <Route path="/" element={<Help />} />
              <Route path="/subscriptions" element={<Subscriptions />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/support" element={<Support />} />
              <Route path="/download" element={<Download />} />
              <Route path="/help" element={<Help />} />
              <Route path="/home" element={<Home />} />
              {user?.is_admin && <Route path="/admin" element={<Admin />} />}
            </Routes>
          </div>
          <nav className="App-nav">
            <NavLink to="/subscriptions" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}><FaBook className="gradient-icon" /> <span>Подписки</span></NavLink>
            <NavLink to="/profile" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}><FaUser className="gradient-icon" /> <span>Кабинет</span></NavLink>
            <NavLink 
              to="/help" 
              className={({ isActive }) => "nav-link logo-link" + (isActive || location.pathname === '/' ? " active" : "")}
            >
              <Logo />
            </NavLink>
            <NavLink to="/support" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}><FaPaperPlane className="gradient-icon" /> <span>Поддержка</span></NavLink>
            <NavLink to="/download" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}><FaDownload className="gradient-icon" /> <span>Скачать</span></NavLink>
            {user?.is_admin && (
              <NavLink to="/admin" className={({ isActive }) => "nav-link" + (isActive ? " active" : "")}><FaUserShield className="gradient-icon" /> <span>Админка</span></NavLink>
            )}
          </nav>
        </div>
      </UserUpdaterContext.Provider>
    </UserContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;